import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VAULTS } from "@/lib/config";
import * as Icons from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { QRCodeCanvas } from "qrcode.react";
import BusinessCardModal from "@/components/BusinessCardModal";
import AuthImage from "@/components/AuthImage";
import { buildVCard } from "@/lib/vcard";

const Icon = ({ name, ...p }) => { const C = Icons[name] || Icons.Circle; return <C {...p} />; };

const daysUntil = (d) => Math.ceil((new Date(d) - new Date()) / 86400000);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [cardOpen, setCardOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const profile = user?.profile || {};
  const company = profile.company || "Renova Eco Systems";
  const initials = (user?.name || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const hasCardInfo = !!(profile.title || profile.phone || profile.work_email || profile.avatar_file_id);
  const vcard = buildVCard({ name: user?.name, email: user?.email, profile });

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]">
      <header className="mb-8">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-400">Overview</p>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-1">Renova Eco Systems — Digital Vault</h1>
        <p className="text-sm text-gray-500 mt-1">A living digital model of the company — {data.total} assets under management.</p>
      </header>

      {/* Digital business card banner */}
      <div data-testid="dashboard-card-banner"
        className="mb-6 rounded-2xl bg-[#1B4332] text-white p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between overflow-hidden">
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-14 h-14 rounded-2xl ring-2 ring-white/20 bg-white/10 overflow-hidden flex items-center justify-center shrink-0">
            <AuthImage fileId={profile.avatar_file_id} alt={user?.name} className="w-full h-full object-cover"
              fallback={<span className="text-lg font-bold">{initials}</span>} />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-white/50 font-semibold">My business card</p>
            <h3 className="font-heading text-xl font-extrabold truncate">{user?.name}</h3>
            <p className="text-sm text-white/70 truncate">{profile.title ? `${profile.title} · ${company}` : company}</p>
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <div className="bg-white p-1.5 rounded-lg hidden sm:block">
            <QRCodeCanvas value={vcard} size={56} level="M" fgColor="#1B4332" />
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setCardOpen(true)} className="bg-white text-[#1B4332] hover:bg-white/90" data-testid="view-card-btn">
              <Icons.QrCode className="w-4 h-4 mr-1.5" /> View &amp; share
            </Button>
            {!hasCardInfo && (
              <Button variant="ghost" onClick={() => navigate("/settings")}
                className="text-white hover:bg-white/10 border border-white/20" data-testid="setup-card-btn">
                Set up
              </Button>
            )}
          </div>
        </div>
      </div>

      <BusinessCardModal open={cardOpen} onOpenChange={setCardOpen}
        name={user?.name} email={user?.email} role={user?.role} profile={profile} />

      {/* Vault cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {Object.entries(VAULTS).map(([key, v]) => (
          <button key={key} data-testid={`vault-card-${key}`} onClick={() => navigate(`/vault/${key}`)}
            className="text-left bg-white border border-gray-200 rounded-md p-5 hover:shadow-md transition-shadow group">
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: `${v.color}15` }}>
                <Icon name={v.icon} className="w-5 h-5" style={{ color: v.color }} />
              </div>
              <Icons.ArrowUpRight className="w-4 h-4 text-gray-300 group-hover:text-gray-600 transition-colors" />
            </div>
            <div className="mt-4 font-heading text-3xl font-extrabold">{data.counts[key] ?? 0}</div>
            <div className="text-sm font-medium mt-1">{v.label}</div>
            <div className="text-xs text-gray-400 mt-0.5">{v.tagline}</div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Expiring */}
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-white border border-gray-200 rounded-md">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
              <Icons.CalendarClock className="w-4 h-4 text-amber-500" />
              <h3 className="font-heading font-semibold">Expiration tracking</h3>
            </div>
            <div className="divide-y divide-gray-100">
              {data.expired.map((it) => (
                <Row key={it.id} it={it} navigate={navigate} label="Expired" tone="red" days={daysUntil(it.expiration_date)} />
              ))}
              {data.expiring.map((it) => (
                <Row key={it.id} it={it} navigate={navigate} label={`${daysUntil(it.expiration_date)}d left`} tone="amber" days={daysUntil(it.expiration_date)} />
              ))}
              {data.expired.length === 0 && data.expiring.length === 0 && (
                <div className="px-5 py-10 text-center text-sm text-gray-400">Nothing expiring in the next 60 days.</div>
              )}
            </div>
          </section>

          <section className="bg-white border border-gray-200 rounded-md">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
              <Icons.ShieldAlert className="w-4 h-4 text-red-500" />
              <h3 className="font-heading font-semibold">Security status</h3>
            </div>
            <div className="divide-y divide-gray-100">
              {data.warnings.length === 0 && (
                <div className="px-5 py-10 text-center text-sm text-gray-400">No security warnings. All secrets look healthy.</div>
              )}
              {data.warnings.map((w) => (
                <button key={w.id} onClick={() => navigate(`/item/${w.id}`)}
                  className="w-full text-left px-5 py-3 flex items-center justify-between hover:bg-gray-50">
                  <span className="text-sm font-medium">{w.title}</span>
                  <Badge variant="outline" className="text-red-600 border-red-200 bg-red-50">
                    <Icons.AlertTriangle className="w-3 h-3 mr-1" />{w.issue}
                  </Badge>
                </button>
              ))}
            </div>
          </section>
        </div>

        {/* Activity */}
        <section className="bg-white border border-gray-200 rounded-md">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
            <Icons.Activity className="w-4 h-4 text-[#1B4332]" />
            <h3 className="font-heading font-semibold">Recent activity</h3>
          </div>
          <div className="p-3 space-y-1 max-h-[520px] overflow-y-auto vault-scroll">
            {data.activity.length === 0 && <div className="px-2 py-8 text-center text-sm text-gray-400">No activity yet.</div>}
            {data.activity.map((a) => (
              <div key={a.id} className="flex gap-3 px-2 py-2.5 rounded-md hover:bg-gray-50">
                <div className="w-7 h-7 shrink-0 rounded-full bg-gray-100 flex items-center justify-center text-[11px] font-semibold text-gray-600">
                  {(a.user_name || "?")[0]}
                </div>
                <div className="min-w-0">
                  <p className="text-sm leading-snug">{a.detail}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">{a.user_name} · {new Date(a.created_at).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function Row({ it, navigate, label, tone }) {
  const tones = { red: "text-red-600 border-red-200 bg-red-50", amber: "text-amber-600 border-amber-200 bg-amber-50" };
  return (
    <button onClick={() => navigate(`/item/${it.id}`)} className="w-full text-left px-5 py-3 flex items-center justify-between hover:bg-gray-50">
      <div>
        <div className="text-sm font-medium">{it.title}</div>
        <div className="text-xs text-gray-400 capitalize">{it.type} · {new Date(it.expiration_date).toLocaleDateString()}</div>
      </div>
      <Badge variant="outline" className={tones[tone]}>{label}</Badge>
    </button>
  );
}
